import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
  Box,
  TextField,
  Button,
  Grid,
  Typography,
  Paper,
  Autocomplete,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { selectStationsStatesData } from '../../../store/stationsAndUnitsReducer/selectors';
import ButtonTheme from '../../../components/ThemeButton/ThemeButton';

const StationForm = ({ station, onSubmit, onCancel, isEdit = false }) => {
  const { stationsStatesEnum, isStationsStatesEnumLoading } = useSelector(selectStationsStatesData);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isDirty },
    reset,
    setValue,
    watch,
    trigger,
  } = useForm({
    defaultValues: {
      address_line_1: '',
      address_line_2: '',
      city: '',
      state: '',
      zip_code: '',
      station_id: '',
      internal_id: '',
      staffing: 0,
      location: '',
    },
  });

  const watchedState = watch('state');

  useEffect(() => {
    if (station) {
      // Set form values when station data is provided
      Object.keys(station).forEach((key) => {
        if (key !== 'id' && key !== 'created_at' && key !== 'updated_at' && key !== 'tenant_id') {
          setValue(key, station[key] || '');
        }
      });
    }
  }, [station, setValue]);

  const handleFormSubmit = (data) => {
    // Convert staffing to number if it's a string
    const formData = {
      ...data,
      staffing: parseInt(data.staffing) || 0,
    };
    onSubmit(formData);
  };

  const handleCancel = () => {
    reset();
    onCancel?.();
  };

  // Disable submit button if form is not dirty (for edit mode)
  const isSubmitDisabled = isSubmitting || (isEdit && !isDirty);

  return (
    <Paper sx={{ p: 3, mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        {isEdit ? 'Edit Station' : 'Create New Station'}
      </Typography>

      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <Grid container spacing={3}>
          {/* Station ID */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Station ID"
              {...register('station_id', {
                required: 'Station ID is required',
              })}
              error={!!errors.station_id}
              helperText={errors.station_id?.message}
            />
          </Grid>

          {/* Internal ID */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Internal ID"
              {...register('internal_id', {
                required: 'Internal ID is required',
              })}
              error={!!errors.internal_id}
              helperText={errors.internal_id?.message}
            />
          </Grid>

          {/* Address Line 1 */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Address Line 1"
              {...register('address_line_1', {
                required: 'Address Line 1 is required',
              })}
              error={!!errors.address_line_1}
              helperText={errors.address_line_1?.message}
            />
          </Grid>

          {/* Address Line 2 */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Address Line 2"
              {...register('address_line_2')}
              error={!!errors.address_line_2}
              helperText={errors.address_line_2?.message}
            />
          </Grid>

          {/* City */}
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="City"
              {...register('city', {
                required: 'City is required',
              })}
              error={!!errors.city}
              helperText={errors.city?.message}
            />
          </Grid>

          {/* State */}
          <Grid item xs={12} md={4}>
            <Autocomplete
              options={stationsStatesEnum || []}
              value={watchedState || null}
              onChange={(event, newValue) => {
                setValue('state', newValue || '', { shouldValidate: true });
              }}
              onInputChange={(event, newInputValue) => {
                if (newInputValue && !stationsStatesEnum?.includes(newInputValue)) {
                  setValue('state', '', { shouldValidate: true });
                }
              }}
              loading={isStationsStatesEnumLoading}
              disabled={isStationsStatesEnumLoading}
              freeSolo={false}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="State"
                  required
                  error={!!errors.state}
                  helperText={errors.state?.message}
                  {...register('state', {
                    required: 'State is required',
                    validate: (value) => {
                      if (!value) return 'State is required';
                      if (!stationsStatesEnum?.includes(value)) {
                        return 'Please select a valid state from the list';
                      }
                      return true;
                    },
                  })}
                />
              )}
              isOptionEqualToValue={(option, value) => option === value}
              getOptionLabel={(option) => option || ''}
            />
          </Grid>

          {/* Zip Code */}
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Zip Code"
              {...register('zip_code', {
                required: 'Zip Code is required',
              })}
              error={!!errors.zip_code}
              helperText={errors.zip_code?.message}
            />
          </Grid>

          {/* Location */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Location"
              {...register('location')}
              error={!!errors.location}
              helperText={errors.location?.message}
            />
          </Grid>

          {/* Staffing */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="number"
              label="Staffing"
              {...register('staffing', {
                min: { value: 0, message: 'Staffing must be 0 or greater' },
                valueAsNumber: true,
              })}
              error={!!errors.staffing}
              helperText={errors.staffing?.message}
            />
          </Grid>

          {/* Action Buttons */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={handleCancel}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <ButtonTheme
                type="submit"
                variant="filled"
                color="primary"
                disabled={isSubmitDisabled}
              >
                {isSubmitting ? 'Saving...' : isEdit ? 'Update Station' : 'Create Station'}
              </ButtonTheme>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Paper>
  );
};

export default StationForm; 